import { useRef, useMemo, useEffect, useReducer, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'src/hooks/use-translation';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import posthog from 'src/utils/posthog';

import { DEVELOPMENT } from 'src/config-global';
import { PATHS } from 'src/routes/paths';
import {
  ConfirmEmailForm,
  ForgotPasswordForm,
  LoginForm,
  RegisterForm,
  ResetPasswordForm,
  ErrorResponse,
  Platform,
  AUTH_ERROR_CODES,
  AuthTokens,
  User,
} from 'src/types';
import {
  login as loginApi,
  register as registerApi,
  forgotPassword as forgotPasswordApi,
  resetPassword as resetPasswordApi,
  confirmEmail as confirmEmailApi,
  logout as logoutApi,
  getUserProfile,
} from 'src/api/auth';
import { updateLastActive } from 'src/api/member';
import { PENDING_INVITATION_KEY } from 'src/utils/constants';
import { setTokens, clearTokens, getAccessToken } from 'src/utils/token-storage';
import { clearPlatformCache } from 'src/utils/platform-cache';
import { setUnauthorizedHandler } from 'src/utils/axios';
import { useNavigate } from 'src/hooks/use-navigate';
import { isSuccessResponse } from 'src/utils/response-type-guards';
import { runWithCallbacks } from 'src/utils/run-with-callbacks';
import { useSnackbar } from 'src/components/snackbar';

import { AuthContext } from './auth-context';
import { AuthUserType, ActionMapType, AuthStateType } from '../types';

// ----------------------------------------------------------------------

const AUTH_ROUTE_PATHS: string[] = Object.values(PATHS.auth);
const isPublicAuthRoute = () => AUTH_ROUTE_PATHS.includes(window.location.pathname);

const Types = {
  INITIAL: 'INITIAL',
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  CHANGE_PLATFORM: 'CHANGE_PLATFORM',
  UPDATE_USER: 'UPDATE_USER',
} as const;

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
  [Types.CHANGE_PLATFORM]: {
    platformId: Platform['id'];
  };
  [Types.UPDATE_USER]: {
    user: User;
  };
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  user: null,
  loading: true,
  platformId: '',
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      ...state,
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      user: action.payload.user,
    };
  }

  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null,
      platformId: '',
    };
  }
  if (action.type === Types.CHANGE_PLATFORM) {
    return {
      ...state,
      platformId: action.payload.platformId,
    };
  }
  if (action.type === Types.UPDATE_USER) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  return state;
};

// ----------------------------------------------------------------------
type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);
  const unauthorizedHandledRef = useRef(false);
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation('', { keyPrefix: 'auth' });
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Capture invitation_id synchronously during render (not in an effect) so it lands in
  // sessionStorage before ANY effect fires — including AuthGuard's redirect-to-login effect,
  // which reads window.location.search directly and does not know about invitation_id embedded
  // in returnTo. Capturing during render removes that race entirely.
  const invitationCapturedRef = useRef(false);
  if (!invitationCapturedRef.current) {
    invitationCapturedRef.current = true;
    const invitationIdFromUrl = new URLSearchParams(window.location.search).get('invitation_id');
    if (invitationIdFromUrl) {
      sessionStorage.setItem(PENDING_INVITATION_KEY, invitationIdFromUrl);
    }
  }

  const initialize = useCallback(async () => {
    // Design sandbox (dev-only) runs without auth. Skip the profile fetch so its
    // 401 doesn't trigger the global unauthorized handler and bounce to login.
    if (DEVELOPMENT && window.location.pathname.startsWith('/_design')) {
      dispatch({ type: Types.INITIAL, payload: { user: null } });
      return;
    }

    if (!getAccessToken()) {
      dispatch({ type: Types.INITIAL, payload: { user: null } });
      return;
    }
    const onSuccess = (data: AuthUserType) => {
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: data,
        },
      });
      if (data?.id) {
        posthog.identify(String(data.id), {
          email: data.email,
          name: `${data.first_name} ${data.last_name}`,
          language: 'uz',
        });
      }
    };
    const onError = () => {
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
        },
      });
    };
    runWithCallbacks(getUserProfile, onSuccess, onError);
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const handleUnauthorized = useCallback(() => {
    // Design sandbox (dev-only) runs without auth — background 401s from app
    // providers must not bounce it to the login page.
    if (DEVELOPMENT && window.location.pathname.startsWith('/_design')) return;
    if (isPublicAuthRoute()) return;
    if (unauthorizedHandledRef.current) return;
    unauthorizedHandledRef.current = true;

    clearTokens();
    clearPlatformCache();
    dispatch({ type: Types.LOGOUT });
    // Clear all cached queries to avoid leaking previous user's data
    queryClient.clear();
    showSnackbar({ message: t('session-expired'), severity: 'warning' });
    navigate(PATHS.auth.login, { replace: true });
  }, [navigate, queryClient, showSnackbar, t]);

  useEffect(() => {
    setUnauthorizedHandler(handleUnauthorized);
    return () => setUnauthorizedHandler(null);
  }, [handleUnauthorized]);

  useEffect(() => {
    if (state.user) {
      unauthorizedHandledRef.current = false;
    }
  }, [state.user]);

  useEffect(() => {
    const platformIdInUrl = searchParams.get('platform_id') || '';
    if (!platformIdInUrl) return;
    if (platformIdInUrl === state.platformId) return;

    dispatch({
      type: Types.CHANGE_PLATFORM,
      payload: {
        platformId: platformIdInUrl,
      },
    });
  }, [searchParams, state.platformId]);

  // Store invitation_id from URL into sessionStorage and clean URL
  useEffect(() => {
    const invitationId = searchParams.get('invitation_id');
    if (!invitationId) return;

    sessionStorage.setItem(PENDING_INVITATION_KEY, invitationId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('invitation_id');
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  // Sync active platform to PostHog so feature flags can target platform groups
  useEffect(() => {
    if (state.platformId) {
      posthog.group('platform', state.platformId);
    }
  }, [state.platformId]);

  // Identify user in Sentry for better error attribution
  useEffect(() => {
    if (state.loading) return;

    if (state.user) {
      Sentry.setUser({ id: state.user.id });
    } else {
      Sentry.setUser(null);
    }
  }, [state.loading, state.user]);

  // Track user activity and send last-active ping every 5 minutes
  const isUserActive = useRef(false);

  useEffect(() => {
    if (!state.user || !state.platformId) return;

    const markActive = () => {
      isUserActive.current = true;
    };

    const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
      'mousemove',
      'keydown',
      'click',
      'scroll',
      'touchstart',
    ];

    ACTIVITY_EVENTS.forEach((event) => document.addEventListener(event, markActive, true));

    // Send immediately on mount, then every 5 minutes
    updateLastActive().catch(() => {});

    const FIVE_MINUTES = 5 * 60 * 1000;

    const intervalId = setInterval(() => {
      if (isUserActive.current) {
        isUserActive.current = false;
        updateLastActive().catch(() => {});
      }
    }, FIVE_MINUTES);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => document.removeEventListener(event, markActive, true));
      clearInterval(intervalId);
    };
  }, [state.user, state.platformId]);

  // LOGIN
  const login = useCallback(
    async (data: LoginForm) => {
      queryClient.clear();
      clearPlatformCache();
      const onSuccess = async (tokens: AuthTokens) => {
        setTokens(tokens.access_token, tokens.refresh_token);

        // Fetch user profile after storing tokens
        const profileResponse = await getUserProfile();

        if (!isSuccessResponse(profileResponse)) {
          clearTokens();
          dispatch({ type: Types.LOGOUT });
          return;
        }

        dispatch({
          type: Types.LOGIN,
          payload: {
            user: profileResponse,
          },
        });
        posthog.identify(String(profileResponse.id), {
          email: profileResponse.email,
          name: `${profileResponse.first_name} ${profileResponse.last_name}`,
          language: 'uz',
        });
        posthog.capture('user_logged_in');
      };
      const onError = (err: ErrorResponse) => {
        console.error('err', err.message);
        if (err.code === AUTH_ERROR_CODES.INCORRECT_EMAIL_OR_PASSWORD) {
          showSnackbar({
            message: t('incorrect-email-or-password'),
            severity: 'error',
          });
        } else if (err.code === AUTH_ERROR_CODES.TOO_MANY_ATTEMPTS) {
          showSnackbar({
            message: t('too-many-attempts'),
            severity: 'error',
          });
        } else {
          showSnackbar({ message: err.message, severity: 'error' });
        }
      };

      await runWithCallbacks(() => loginApi(data), onSuccess, onError);
    },
    [t, showSnackbar, queryClient]
  );

  // REGISTER
  const register = useCallback(
    async (data: RegisterForm) => {
      const onSuccess = () => {
        posthog.capture('user_registered');
        navigate(PATHS.auth.confirmEmail, { state: { email: data.email } });
      };
      const onError = (err: ErrorResponse) => {
        console.error('err', err.message);
        if (err.code === AUTH_ERROR_CODES.EMAIL_ALREADY_IN_USE) {
          showSnackbar({
            message: t('email-already-in-use'),
            severity: 'error',
          });
        } else {
          showSnackbar({ message: err.message, severity: 'error' });
        }
      };
      await runWithCallbacks(() => registerApi(data), onSuccess, onError);
    },
    [navigate, t, showSnackbar]
  );

  // CONFIRM EMAIL
  const confirmEmail = useCallback(
    async (data: ConfirmEmailForm) => {
      const onSuccess = async (tokens: AuthTokens) => {
        setTokens(tokens.access_token, tokens.refresh_token);

        // Fetch user profile after storing tokens
        const profileResponse = await getUserProfile();

        if (!isSuccessResponse(profileResponse)) {
          clearTokens();
          dispatch({ type: Types.LOGOUT });
          return;
        }

        dispatch({
          type: Types.LOGIN,
          payload: {
            user: profileResponse,
          },
        });
        posthog.identify(String(profileResponse.id), {
          email: profileResponse.email,
          name: `${profileResponse.first_name} ${profileResponse.last_name}`,
        });
      };
      const onError = (err: ErrorResponse) => {
        console.error(err.message);
        if (err.code === AUTH_ERROR_CODES.INCORRECT_CONFIRM_CODE) {
          showSnackbar({
            message: t('incorrect-confirm-code'),
            severity: 'error',
          });
        } else {
          showSnackbar({ message: err.message, severity: 'error' });
        }
      };
      await runWithCallbacks(() => confirmEmailApi(data), onSuccess, onError);
    },
    [t, showSnackbar]
  );

  // FORGOT PASSWORD
  const forgotPassword = useCallback(
    async (data: ForgotPasswordForm) => {
      const onSuccess = () => {
        navigate(PATHS.auth.newPassword, { state: { email: data.email } });
      };
      const onError = (err: ErrorResponse) => {
        console.error(err.message);
        if (err.code === AUTH_ERROR_CODES.EMAIL_NOT_FOUND) {
          showSnackbar({
            message: t('email-not-found'),
            severity: 'error',
          });
        } else {
          showSnackbar({ message: err.message, severity: 'error' });
        }
      };
      await runWithCallbacks(() => forgotPasswordApi(data), onSuccess, onError);
    },
    [navigate, t, showSnackbar]
  );

  // RESET PASSWORD
  const resetPassword = useCallback(
    async (data: ResetPasswordForm) => {
      const onSuccess = () => {
        navigate(PATHS.auth.login, { state: undefined });
      };
      const onError = (err: ErrorResponse) => {
        console.error(err.message);
        if (err.code === AUTH_ERROR_CODES.INCORRECT_RESET_CODE) {
          showSnackbar({
            message: t('incorrect-reset-code'),
            severity: 'error',
          });
        } else {
          showSnackbar({ message: err.message, severity: 'error' });
        }
      };
      await runWithCallbacks(() => resetPasswordApi(data), onSuccess, onError);
    },
    [navigate, t, showSnackbar]
  );

  // LOGOUT
  const logout = useCallback(async () => {
    const onSuccess = () => {
      clearTokens();
      clearPlatformCache();
      dispatch({
        type: Types.LOGOUT,
      });
      // Clear all cached queries to avoid leaking previous user's data
      queryClient.clear();
      posthog.reset();
    };
    const onError = (err: ErrorResponse) => {
      console.error(err.message);
    };
    await runWithCallbacks(logoutApi, onSuccess, onError);
  }, [queryClient]);

  const changeActivePlatform = useCallback(
    (nextPlatformId: Platform['id']) => {
      dispatch({
        type: Types.CHANGE_PLATFORM,
        payload: { platformId: nextPlatformId },
      });

      const next = new URLSearchParams(window.location.search);
      next.set('platform_id', nextPlatformId);
      navigate(`${window.location.pathname}?${next.toString()}`, { replace: true });
    },
    [navigate]
  );

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const updateUser = useCallback((user: User) => {
    dispatch({ type: Types.UPDATE_USER, payload: { user } });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'default',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      platformId: state.platformId,
      login,
      register,
      confirmEmail,
      forgotPassword,
      resetPassword,
      logout,
      changeActivePlatform,
      updateUser,
    }),
    [
      updateUser,
      login,
      register,
      confirmEmail,
      forgotPassword,
      resetPassword,
      logout,
      changeActivePlatform,
      state.user,
      state.platformId,
      status,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
